<script setup lang="ts">
/**
 * La page « Option Avancé et SMS » (écran 9 du dossier), en lecture (lot F5).
 *
 * Ce que l'utilisateur doit vivre : il lit ce que comprend chaque niveau,
 * l'offre de son entreprise et l'essai en cours, son solde de SMS (« solde
 * non disponible » quand il est inconnu, jamais 0) et, s'il est propriétaire
 * ou administrateur, les mouvements du solde. Au Basique, une phrase lui dit
 * de demander l'essai au support : aucun bouton, aucune route ne transmet la
 * demande. Aucun prix, aucune recharge.
 *
 * Les accès viennent de la matrice par `useAcces` (gestes `offre_voir_niveaux`,
 * `offre_essayer_avance`, `sms_voir_solde`, `sms_voir_mouvements`). Une offre
 * non lue se dit à sa place ; tant qu'elle n'est pas lue, aucun geste n'est
 * montré. Hors de l'offre Energy Manager, `CadreModule` rend l'écran hors
 * offre et rien n'est lu.
 */
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import {
    cleDeSession,
    useOuvertureStore,
} from "@modules/alarmes-v2/stores/ouverture.store"
import {
    useSmsStore,
    type StatutSms,
} from "@modules/alarmes-v2/stores/sms.store"
import { formaterDate } from "@modules/alarmes-v2/utils/mesure"
import type { AlarmesV2Sms } from "@modules/alarmes-v2/types/alarmes-v2"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

/**
 * La session connectée.
 */
const authStore = useAuthStore()

/**
 * L'ouverture du module et l'offre lue.
 */
const ouvertureStore = useOuvertureStore()

/**
 * Le solde de SMS relu par la page.
 */
const smsStore = useSmsStore()

/**
 * Vrai quand la relecture du solde vaut pour la session courante.
 */
const getSmsPourLaSession = computed(
    (): boolean => smsStore.cleLue !== "" && smsStore.cleLue === cleDeSession()
)

/**
 * Le solde relu pour la session, `null` sinon.
 */
const getSms = computed((): AlarmesV2Sms | null =>
    getSmsPourLaSession.value ? smsStore.sms : null
)

/**
 * Où en est la relecture du solde pour la session.
 */
const getStatutSms = computed(
    (): StatutSms => (getSmsPourLaSession.value ? smsStore.statut : "attente")
)

/**
 * Les accès : le solde relu, une fois lu, remplace celui de l'offre.
 */
const { acces } = useAcces({
    sms: () => (getStatutSms.value === "pret" ? getSms.value : undefined),
})

/**
 * L'offre lue pour la session, `null` sinon.
 */
const getOffre = computed(() =>
    ouvertureStore.luPourLaSession && ouvertureStore.offreLue
        ? ouvertureStore.offre
        : null
)

/**
 * Vrai quand la page doit dire que l'offre n'a pas pu être lue.
 */
const getOffreNonLue = computed(
    (): boolean =>
        ouvertureStore.luPourLaSession &&
        ouvertureStore.ouvert &&
        !ouvertureStore.offreLue
)

/**
 * La ligne de l'essai en cours, `null` sans essai.
 */
const getEssai = computed((): string | null => {
    const fin = getOffre.value?.essai_jusqu_au
    if (fin === undefined || fin === null) return null
    const date = formaterDate(fin)
    return date
        ? t("alarmesV2.option.offre.essaiEnCours", { date })
        : t("alarmesV2.option.offre.essaiSansDate")
})

/**
 * Les verdicts des quatre gestes de l'écran.
 */
const getVoirNiveaux = computed(() => acces("offre_voir_niveaux"))
const getEssayer = computed(() => acces("offre_essayer_avance"))
const getVoirSolde = computed(() => acces("sms_voir_solde"))
const getVoirMouvements = computed(() => acces("sms_voir_mouvements"))

/**
 * Relit le solde quand l'offre lue est l'option Avancé : au Basique le solde
 * n'est pas montré, il n'est pas demandé.
 */
function lire(): void {
    if (!authStore.hasOfferEnergyManager) return
    const abonnement = ouvertureStore.abonnement
    if (abonnement === "em_avance" || abonnement === "em_avance_solde_epuise")
        void smsStore.charger()
}

// Relu à chaque entrée sur la page, et quand la session ou l'offre change.
watch(
    () =>
        [
            authStore.companyId,
            authStore.userId,
            authStore.hasOfferEnergyManager,
            ouvertureStore.abonnement,
        ] as const,
    lire,
    { immediate: true }
)

/**
 * Relit l'offre, puis le solde, après une offre non lue.
 */
async function onRelire(): Promise<void> {
    await ouvertureStore.charger({ relireOffre: true })
    lire()
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreModule>
        <div class="mx-auto flex max-w-5xl flex-col gap-4 p-4 md:p-6">
            <!-- ================================ -->
            <!-- EN-TÊTE -->
            <!-- ================================ -->
            <header>
                <h1 class="text-2xl font-semibold text-surface-900">
                    {{ t("alarmesV2.option.titre") }}
                </h1>
                <p class="mt-1 text-sm text-surface-700">
                    {{ t("alarmesV2.option.intro") }}
                </p>
            </header>

            <!-- ================================ -->
            <!-- L'OFFRE DE L'ENTREPRISE -->
            <!-- ================================ -->
            <section
                v-if="getOffreNonLue"
                class="flex flex-wrap items-center gap-3 rounded-lg border border-surface-200 bg-surface-0 px-4 py-3"
                data-test="offre-non-lue"
            >
                <p class="text-sm text-surface-800">
                    {{ t("alarmesV2.option.offre.nonLue") }}
                </p>
                <button
                    type="button"
                    class="text-sm font-medium text-primary-700 underline"
                    data-test="relire"
                    @click="onRelire"
                >
                    {{ t("alarmesV2.refus.relire") }}
                </button>
            </section>
            <section
                v-else-if="getOffre && getVoirNiveaux.ok"
                class="rounded-lg border border-surface-200 bg-surface-0 px-4 py-3"
                data-test="offre"
            >
                <template v-if="getOffre.niveau === 'basique'">
                    <h2 class="text-base font-semibold text-surface-900">
                        {{ t("alarmesV2.option.offre.basique") }}
                    </h2>
                    <p class="text-xs text-surface-600">
                        {{ t("alarmesV2.option.offre.basiqueSousTitre") }}
                    </p>
                    <p class="mt-2 text-sm text-surface-700">
                        {{ t("alarmesV2.option.offre.basiqueTexte") }}
                    </p>
                    <p
                        v-if="getEssayer.affichage !== 'absent'"
                        class="mt-3 text-sm text-surface-700"
                        data-test="essai-phrase"
                    >
                        {{ t("alarmesV2.option.offre.essaiPhrase") }}
                        <span
                            v-if="!getEssayer.ok && getEssayer.raison"
                            class="mt-0.5 block text-xs text-surface-500"
                            data-test="essai-raison"
                            >{{ getEssayer.raison }}</span
                        >
                    </p>
                </template>
                <template v-else>
                    <h2 class="text-base font-semibold text-surface-900">
                        {{ t("alarmesV2.option.offre.avance") }}
                    </h2>
                    <p
                        v-if="getEssai"
                        class="mt-2 text-sm text-surface-700"
                        data-test="essai-en-cours"
                    >
                        {{ getEssai }}
                    </p>
                </template>
            </section>

            <!-- ================================ -->
            <!-- LE SOLDE ET SES MOUVEMENTS -->
            <!-- ================================ -->
            <div v-if="getOffre" class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <SoldeSms
                    :acces="getVoirSolde"
                    :sms="getSms"
                    :statut="getStatutSms"
                />
                <MouvementsSms
                    :acces="getVoirMouvements"
                    :mouvements="getSms?.mouvements"
                />
            </div>

            <!-- ================================ -->
            <!-- CE QUE COMPREND CHAQUE NIVEAU -->
            <!-- ================================ -->
            <TableauNiveaux v-if="getVoirNiveaux.ok" />
        </div>
    </CadreModule>
</template>
